<template>
  <div class="assigned-users-list">
    <wtr-assigned-users-list
      :list="assignedMaterials"
      :options="{
        totalElements: totalAssignedMaterials,
        pagination: true,
      }"
      :params="paginationParams"
      :subFilters="subFilters"
      :configSubFilters="configSubFilters"
      :showLoader="loading"
      @filter-change="handleFilterChange"
      @size-change="handleSizeChange"
      @page-change="handlePageChange"
    >
      <!-- Assigned Users Table -->
      <template v-slot:list-table>
        <b-table
          thead-class="bg-primary text-light"
          primary-key="assignmentId"
          outlined
          responsive
          hover
          :fields="tableFields"
          :items="assignedMaterials"
          show-empty
          empty-text="No assigned materials found"
          :busy="loading"
          :sort-by.sync="paginationParams.sort"
          :sort-desc.sync="paginationParams.sortDesc"
        >
          <!-- Headers -->
          <template v-slot:head(type)="data">
            <div class="d-inline-flex align-items-center text-nowrap">
              <div class="text-nowrap">{{ data.label }}</div>
              <span
                class="cursor-pointer"
                v-on:click="handleSort('em.type')"
              >
                &nbsp;<i class="fa fa-sort ml-1" aria-hidden="true"></i>
              </span>
            </div>
          </template>
          <template v-slot:head(systemIdNumber)="data">
            <div class="d-inline-flex align-items-center text-nowrap">
              <div class="text-nowrap">{{ data.label }}</div>
              <span
                class="cursor-pointer"
                v-on:click="handleSort('em.system_id_number')"
              >
                &nbsp;<i class="fa fa-sort ml-1" aria-hidden="true"></i>
              </span>
            </div>
          </template>
          <template v-slot:head(assignmentDate)="data">
            <div class="d-inline-flex align-items-center text-nowrap">
              <div class="text-nowrap">{{ data.label }}</div>
              <span
                class="cursor-pointer"
                v-on:click="handleSort('assignment_date')"
              >
                &nbsp;<i class="fa fa-sort ml-1" aria-hidden="true"></i>
              </span>
            </div>
          </template>
          <template v-slot:head(startDate)="data">
            <div class="d-inline-flex align-items-center text-nowrap">
              <div class="text-nowrap">{{ data.label }}</div>
              <span
                class="cursor-pointer"
                v-on:click="handleSort('start_date')"
              >
                &nbsp;<i class="fa fa-sort ml-1" aria-hidden="true"></i>
              </span>
            </div>
          </template>
          <template v-slot:head(deadlineDate)="data">
            <div class="d-inline-flex align-items-center text-nowrap">
              <div class="text-nowrap">{{ data.label }}</div>
              <span
                class="cursor-pointer"
                v-on:click="handleSort('deadline_date')"
              >
                &nbsp;<i class="fa fa-sort ml-1" aria-hidden="true"></i>
              </span>
            </div>
          </template>
          <template v-slot:head(actions)="data">
            <div class="text-nowrap">{{ data.label }}</div>
          </template>

          <!-- Cells -->
          <template v-slot:cell(type)="row">
            <div class="text-nowrap fs-13">
              {{
                row.item.materialType === "Assignment"
                  ? "Unknown Slides Assignment"
                  : ""
              }}
            </div>
          </template>
          <template v-slot:cell(systemIdNumber)="row">
            <div class="text-nowrap fs-13">
              {{ row.item.systemIdNumber || "N/A" }}
            </div>
          </template>
          <template v-slot:cell(assignmentDate)="row">
            <div class="text-nowrap fs-13">
              {{ new Date(row.item.assignmentDate).toLocaleDateString() }}
            </div>
          </template>
          <template v-slot:cell(startDate)="row">
            <div class="text-nowrap fs-13">
              {{ new Date(row.item.startDate).toLocaleDateString() }}
            </div>
          </template>
          <template v-slot:cell(deadlineDate)="row">
            <div class="text-nowrap fs-13">
              {{
                row.item.deadlineDate
                  ? new Date(row.item.deadlineDate).toLocaleDateString()
                  : ""
              }}
            </div>
          </template>
          <template v-slot:cell(actions)="row">
            <table-actions
              :key="row.item.assignmentId"
              :assignment="row.item"
              :viewIcon="{ show: 'View', title: 'View Assignment' }"
              :editRecipientsIcon="{ show: 'Edit', title: 'Edit Recipients' }"
              :shareAnswerIcon="{
                show: 'Share Answer',
                title: 'Share Answer',
              }"
              :viewResponsesIcon="{
                show: 'View Responses',
                title: 'View Responses',
              }"
              :deleteIcon="{ show: 'Delete', title: 'Delete Assignment' }"
              @view="viewAssignedMaterial(row.item)"
              @edit="(event) => $emit('edit-assignment', { event, assignment: row.item })"
              @shareAnswer="shareAnswer(row.item)"
              @viewResponses="viewResponses(row.item)"
              @delete="deleteItem(row.item)"
            ></table-actions>
          </template>
        </b-table>
        </template>
        <template v-slot:empty>
            <tr>
            <td :colspan="tableFields.length" class="text-center py-5">
                <div class="text-muted">
                <i class="fas fa-users fa-2x mb-2"></i>
                <h5>No assigned materials found</h5>
                <p class="mb-0">No assignments have been created yet</p>
                </div>
                </td>
            </tr>
            </template>
    </wtr-assigned-users-list>
  </div>
</template>

<script>
import TableActionsComponent from "../../../../components/common/table-actions.vue";
import WTRAssignedUsersList from "../../../../components/common/wtr-assigned-users-list.vue";
import { createEducationalAssignmentPdf } from "../../../../helpers/educational-assignment-pdf";

export default {
  name: "AssignedUsersList",
  components: {
    TableActions: TableActionsComponent,
     'wtr-assigned-users-list': WTRAssignedUsersList,
  },
  props: {
    currentFolder: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      assignedMaterials: [],
      paginationParams: {
        page: 0,
        size: 10,
        sort: "assignment_date,desc",
        sortDesc: true,
      },
      totalAssignedMaterials: 0,
      loading: false,
      currentFilters: {
        search: "",
        slideCollectionType: "",
      },
      configSubFilters: [
        {
          optionLabel: "name",
          optionValue: "id",
          placeholder: "Learning Modality",
          searchParam: "slideCollectionType",
        },
      ],
      subFilters: [[]],
    };
  },
  computed: {
    tableFields() {
      return [
        { key: "type", label: this.$t("Learning Modality") },
        { key: "systemIdNumber", label: this.$t("System ID") },
        { key: "assignmentDate", label: this.$t("Assignment Date") },
        { key: "startDate", label: this.$t("Start Date") },
        { key: "deadlineDate", label: this.$t("Deadline Date") },
        { key: "actions", label: this.$t("Actions") },
      ];
    },
    staticLearningModalities() {
      return [
        { name: "All", id: "all" },
        { name: "Unknown Slides Assignment", id: "UNKNOWN_SLIDES_ASSIGNMENT" },
      ];
    },
  },
  mounted() {
    this.initializeFilters();
    this.fetchAssignedMaterials();
  },
  methods: {
    initializeFilters() {
      this.subFilters[0] = this.staticLearningModalities;
    },

    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },

    async fetchAssignedMaterials() {
      await this.sleep(100);
      this.loading = true;
      try {
        const queryParams = {
          page: this.paginationParams.page,
          size: this.paginationParams.size,
          sort: this.paginationParams.sort,
          sortDirection: this.paginationParams.sortDesc ? "desc" : "asc",
        };

        const body = { ...this.currentFilters };
        Object.keys(body).forEach((key) => {
          if (
            body[key] === null ||
            body[key] === "" ||
            body[key] === undefined
          ) {
            delete body[key];
          }
        });

        const response = await this.$api.getAssignedToUsersList(
          queryParams,
          body
        );
        if (this.$handleResponse(response)) {
          const payload = response.data.payload;
          this.assignedMaterials = payload?.content || [];
          this.totalAssignedMaterials =
            payload?.totalElements || this.assignedMaterials.length;
          this.$emit("folder-changed");
        }
      } catch (error) {
        this.$handleError(error);
        this.assignedMaterials = [];
        this.totalAssignedMaterials = 0;
      } finally {
        this.loading = false;
      }
    },

    handleSort(sortField) {
      const currentSortField = this.paginationParams.sort.split(",")[0];

      if (currentSortField === sortField) {
        this.paginationParams.sortDesc = !this.paginationParams.sortDesc;
      } else {
        this.paginationParams.sortDesc = false;
      }

      const direction = this.paginationParams.sortDesc ? "desc" : "asc";
      this.paginationParams.sort = `${sortField},${direction}`;
      this.paginationParams.page = 0;

      this.fetchAssignedMaterials();
    },

    handleFilterChange(newFilters) {
      if (Object.keys(newFilters).length === 0) {
        this.currentFilters = {
          search: "",
          slideCollectionType: "",
        };
      } else {
        this.currentFilters = {
          ...this.currentFilters,
          ...newFilters,
        };
      }

      this.paginationParams.page = 0;
      this.fetchAssignedMaterials();
    },

    handleSizeChange(params) {
      this.paginationParams.size = params.size;
      this.paginationParams.page = 0;
      this.fetchAssignedMaterials();
    },

    handlePageChange(params) {
      this.paginationParams.page = params.page;
      this.fetchAssignedMaterials();
    },

    viewAssignedMaterial(assignment) {
      const route = "edu-case-viewer";
      this.$changeRouteToTab({
        name: route,
        params: {
          id: assignment.materialId,
          isPublicSite: true,
          tumorSiteId: assignment.tumorSiteId,
          tumorSiteName: assignment.tumorSiteName,
          type: assignment.materialType,
          backToPublicSite: false,
          activeTab: "none",
          randomId: assignment.randomId,
        },
      });
    },

    async shareAnswer(assignment) {
      this.$deleteConfirmation(
        `Do you want to send answer to all recipients?`,
        async () => {
          try {
            const reportResponse = await this.$api.getAnswerReport(
              assignment.assignmentId,
            );
            if (this.$handleResponse && !this.$handleResponse(reportResponse)) {
              return false;
            }

            const pdfAttachment = await createEducationalAssignmentPdf(
              reportResponse.data.payload,
            );
            const response = await this.$api.shareAnswer(
              assignment.assignmentId,
              pdfAttachment,
            );

            if (this.$handleResponse && !this.$handleResponse(response)) {
              return false;
            }
            this.$toastr("success", "Answer shared successfully!");
          } catch (error) {
            this.$handleError(error);
          }
        }
      );
    },

    viewResponses(assignment) {
      const systemIdNumber = assignment.systemIdNumber;
      const formattedDate = this.formatDateForFolder(assignment.assignmentDate);

      this.$emit("view-responses", {
        systemIdNumber,
        assignmentDate: formattedDate,
        assignment,
      });
    },

    deleteItem(item) {
      this.$deleteConfirmation(
        `Are you sure you want to delete ${item.materialType}?`,
        async () => {
          try {
            const response = await this.$api.deleteAssignment(Number(item.assignmentId));
            if (this.$handleResponse(response)) {
              this.$toastr("success", "Assignment deleted successfully");
              this.fetchAssignedMaterials();
            }
          } catch (error) {
            this.$handleError(error);
          }
        }
      );
    },

    formatDateForFolder(dateString) {
      const date = new Date(dateString);
      const month = date.toLocaleString("en-US", { month: "short" });
      const day = date.getDate();
      const year = date.getFullYear();
      return `${month} ${day}_${year}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.fs-13 {
  font-size: 13px !important;
}
</style>
